// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * This module provides functionality to update user status in an organization.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { z } from 'zod';

/**
 * Schema for validating the API response
 */
const successResponseSchema = z.object({
  data: z.object({ success: z.boolean() }),
  metadata: responseMetadataSchema,
});

/**
 * Promotes a user to admin status in an organization
 */
export async function promoteUserToAdmin(
  organizationKey: string,
  userKey: string
) {
  const url = `/v1/organization/${organizationKey}/users/${userKey}/promote`;
  const response = await api.post(url);
  return parseResponse(successResponseSchema, response.data, {
    schemaName: 'PromoteUserToAdmin',
    endpoint: url,
  });
}

/**
 * Demotes a user from admin status in an organization
 */
export async function demoteUserFromAdmin(
  organizationKey: string,
  userKey: string
) {
  const url = `/v1/organization/${organizationKey}/users/${userKey}/demote`;
  const response = await api.post(url);
  return parseResponse(successResponseSchema, response.data, {
    schemaName: 'DemoteUserFromAdmin',
    endpoint: url,
  });
}

/**
 * Deactivates a user's account in an organization
 */
export async function deactivateUser(organizationKey: string, userKey: string) {
  const url = `/v1/organization/${organizationKey}/users/${userKey}/deactivate`;
  const response = await api.post(url);
  return parseResponse(successResponseSchema, response.data, {
    schemaName: 'DeactivateUser',
    endpoint: url,
  });
}

/**
 * Activates a deactivated user's account in an organization
 */
export async function activateUser(organizationKey: string, userKey: string) {
  const url = `/v1/organization/${organizationKey}/users/${userKey}/activate`;
  const response = await api.post(url);
  return parseResponse(successResponseSchema, response.data, {
    schemaName: 'ActivateUser',
    endpoint: url,
  });
}
